import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldAlert } from 'lucide-react';
import { RabbitLoader } from '@/components/ui/rabbit-loader';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useAuth } from '@/contexts/AuthContext';
import { useIdleLogout } from '@/hooks/useIdleLogout';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { toast } from 'sonner';

const IDLE_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
const IDLE_WARNING_MS = 60 * 1000; // 1 minute warning

/**
 * Mounted inside AppLayout — only active for authenticated sessions.
 * Combines:
 *  - Auto-logout after 30 min of inactivity (with 1 min warning modal)
 *  - Persistent offline indicator banner
 */
export function SessionGuards() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const isOnline = useOnlineStatus();

  const handleLogout = useCallback(async () => {
    toast.info('Session expirée', {
      description: 'Vous avez été déconnecté après 30 minutes d\'inactivité.',
      duration: 6000,
    });
    await signOut();
    navigate('/auth', { replace: true });
  }, [signOut, navigate]);

  const { warningOpen, secondsRemaining, stayConnected } = useIdleLogout({
    enabled: !!user,
    timeoutMs: IDLE_TIMEOUT_MS,
    warningMs: IDLE_WARNING_MS,
    onLogout: handleLogout,
  });

  return (
    <>
      {!isOnline && (
        <div
          role="status"
          aria-live="polite"
          aria-busy="true"
          // Full-screen blocker: prevents any click on modules while offline
          className="fixed inset-0 z-[9999]"
          onClickCapture={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <RabbitLoader message="Pas de connexion internet — en attente du réseau…" />
        </div>
      )}

      <AlertDialog open={warningOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-amber-500" />
              Vous êtes toujours là ?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Pour protéger vos données, vous serez automatiquement déconnecté dans{' '}
              <span className="font-semibold text-foreground">
                {secondsRemaining}s
              </span>{' '}
              en raison d'inactivité.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => void handleLogout()}>
              Me déconnecter
            </AlertDialogCancel>
            <AlertDialogAction onClick={stayConnected}>
              Rester connecté
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}